import {
  ChartType,
  COUNT_USERS_ONCE_TYPE,
  FilteredBreakdownSegments,
  IndividualSelectedSegments,
  MATH_TYPE,
  PER_USER_AGGREGATIONS,
  PropertyType,
  ValueRepresentationType,
} from 'common/report/constants';
import {ComparisonDateRangeBookmarkFormat} from 'common/query-builder/query-block/time-comparison-menu/types';
import {BaseDate, DateRangeType, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {Behavior, CustomProperty, Segmentation} from 'common/types/reports/query';
import {FlatSortConfig, SortBy, SortConfig, SortOrder} from 'common/report/util/sorting/types';
import {LiftComparison} from 'common/widgets/lift-comparison-menu/types';
import {BaseQueryErrorType, BaseQueryResultsType, ForecastValue} from 'common/report/types';
import {Bucket} from 'common/widgets/bucket-pane/types';
import {MultiAttribution} from 'common/widgets/multi-attribution-menu/types';
import {MetricType} from 'common/query-builder/query-block/constants';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {SerializedBehaviorDictionary} from 'common/query-builder/cohorts-query-builder/models/types/serialized-cohort-types';
import {Expression} from 'common/query-builder/cohorts-query-builder/models/types/expression-types';
import {BookmarkFunnelExclusionStep, SegmentId} from 'common/report/funnels/models/types';
import {ConversionWindowUnit, FunnelOrder} from 'common/report/funnels/models/constants';
import {ProfileHistoryOptions} from 'common/types/reports/query';
import {ListQuantifier} from 'common/widgets/property-filter-menu/models/types';
/*eslint-disable-next-line import/no-cycle*/
import {RetentionAlignmentType, RetentionType} from 'common/report/retention/util/types';

export type FilterDeterminer = `all` | `any`;

export interface FlatSegment<ValueT = number> {
  key: string;
  path: string[];
  displayName: string;
  displayNamePath?: string[];
  // object key is an ISO-8601 date string for time data
  // or a single arbitrary leaf key for series data e.g. `all`, `value`, `null`
  value: Record<string, ValueT>;
}

export enum TypeOfComparison {
  Time = `time_comparison`,
  Lift = `lift_comparison`,
}

export enum ChartPlotStyle {
  Standard = `standard`,
  Stacked = `stacked`,
}

export enum AnalysisType {
  Linear = `linear`,
  Logarithmic = `logarithmic`,
  Rolling = `rolling`,
  Cumulative = `cumulative`,
}

export interface DisplayOptions {
  chartType: InsightsChartType;
  plotStyle: ChartPlotStyle;
  analysis: AnalysisType;
  value: ValueRepresentationType;
  rollingWindowSize?: number;
  timeUnit?: TimeUnit;

  // Funnels specific stuff
  statSigControl?: SegmentId[];
  funnelStepsSelectedTableColumns?: {
    'conv-first-step': boolean;
    'conv-prev-step': boolean;
    count: boolean;
    'stat-sig': boolean;
    'time-first-step': boolean;
    'time-prev-step': boolean;
  };
}

export type InsightsChartType =
  | ChartType.Bar
  | ChartType.Line
  | ChartType.Pie
  | ChartType.Table
  | ChartType.Metric
  | ChartType.FunnelSteps
  | ChartType.FunnelTopPaths
  | ChartType.RetentionCurve;

export type FlatOrColumnSortConfig = FlatSortConfig | SortConfig;

/** Subset of SortByColumnsConfig:
 * 1) no valueField (used for lift in bar charts, I think)
 * 2) colSortAttrs can only be FlatLabelSortConfig, not FlatValueSortConfig */
export type OldTableSortByColumn = {
  sortBy: SortBy.Column;
  colSortAttrs: Array<{sortBy: SortBy.Label; sortOrder: SortOrder}>;
};

/** Same as SortByValueConfig except:
 * 1) sortColumn (always 'sum') instead of valueField
 * 2) colSortAttrs can only be FlatLabelSortConfig, not FlatValueSortConfig
 *
 * Must convert this for use with data-table
 */
export type OldTableSortByValue = {
  sortBy: SortBy.Value;
  sortOrder: SortOrder;
  sortColumn: 'sum';
  colSortAttrs: Array<{sortBy: SortBy.Label; sortOrder: SortOrder}>;
};

export type OldTableSortConfig = OldTableSortByColumn | OldTableSortByValue;

export type InsightsBookmarkSortConfig = {
  [ChartType.Bar]: SortConfig;
  [ChartType.Table]: SortConfig;
  // In the bookmark, line chart can either have the old FlatSortConfig or the new SortConfig format
  [ChartType.Line]: FlatOrColumnSortConfig;
  [ChartType.Metric]: SortConfig;
  [ChartType.Pie]: SortConfig;
  // Retention specific
  [ChartType.RetentionCurve]: SortConfig;
};

export type InsightsSortConfig = InsightsBookmarkSortConfig & {
  [ChartType.Line]: SortConfig;
};

export enum InsightsResourceType {
  Events = `events`,
  Formulas = `formulas`,
  People = `people`,
  Cohorts = `cohorts`,
  Other = `other`,
}

export interface MetricBehavior {
  //===================================
  // common fields
  //===================================
  type: MetricType;
  // depends on `type`, id could be either
  // cohort id
  // custom-event id
  // behavior id (funnel / retention)
  id?: number;
  name?: string;
  renamed?: string;
  dataGroupId?: string;
  filters?: FilterClause[];
  // controls if the filters should be `and all` or `or any`
  filtersDeterminer?: FilterDeterminer;

  // Nestable self recursive behaviors.
  behaviors?: MetricBehavior[];

  //===================================
  // inline cohort specific
  //===================================
  raw_cohort?: TODO;
  //===================================
  // insight specific
  //===================================
  customBucket?: Bucket;
  //===================================
  // funnel specific
  //===================================
  conversionWindowDuration?: number;
  conversionWindowUnit?: ConversionWindowUnit;
  funnelOrder?: FunnelOrder;
  exclusions?: BookmarkFunnelExclusionStep[];
  aggregateBy?: Segmentation[];
  //===================================
  // retention specific
  //===================================
  retentionType?: RetentionType;
  retentionAlignmentType?: RetentionAlignmentType;
  retentionUnit?: TimeUnit;
  retentionUnbounded?: boolean;
  retentionCustomBucketSizes?: number[];

  // deprecated fields, remove once query-api could parse multi-metric payload without those fields
  unsavedId?: string;
  // backward compatibility, for now it's always `` as empty string
  search?: string;
  resourceType?: InsightsResourceType;
  profileType?: string;
  dataset?: string;
  datasetId?: string;
  projectId?: number;
}

export interface MetricMeasurement {
  // common measurement
  math: MATH_TYPE;
  property?: {
    dataset: string;
    name: string | string[];
    renamed?: string;
    custom?: boolean;
    id?: number;
    type: PropertyType;
    defaultType: PropertyType;
    // seems like sunset feature related
    is_collect_everything_event?: boolean;
    resourceType?: InsightsResourceType;
    raw_cohort?: TODO;
    customPropertyId?: string | number;
    customProperty?: CustomProperty;
    joinPropertyType?: PropertyType;
    tempDataGroupId?: string;
    dataGroupId?: string;
    propertyObjectKey?: string | string[];

    /** Used to specify a unit within a date property. For example, when segmenting on a date */
    unit?: TimeUnit;

    listItemGroup?: Segmentation;
  };
  cumulative?: boolean;
  // simple measurement
  perUserAggregation?: PER_USER_AGGREGATIONS;
  rolling?: {
    rollingWindowSize: number;
  };
  segmentMethod?: COUNT_USERS_ONCE_TYPE;
  multiAttribution?: MultiAttribution;
  // funnels measurement
  stepIndex?: number;
  // 'first_touch' | 'last_touch' | 'step';
  actionMode?: string;
  actionStep?: number;
  // retention measurement
  retentionBucketIndex?: number;
  retentionSegmentationEvent?: string;
}

// New format which show clause will transform into
export interface BehaviorMetric {
  /* behavior */
  behavior: MetricBehavior;
  /* measurement */
  measurement: MetricMeasurement;
  isHidden?: boolean;
  isExpanded?: boolean;
}

/**
 * FormulaMetric is used to represent 3 different types of formulas. Here's how to check for each type:
 * 1. inline formula: `id` is not set
 * 2. saved formula: only `id` is set
 * 3. saved formula with modifications: `name`, `definition`, `referencedMetrics` are set
 */
export type ShowClauseFormula = {
  id?: number;
  definition?: string;
  name?: string;
  referencedMetrics?: BehaviorShowClause[];
};

export type FormulaMeasurement = {
  cumulative?: boolean;
  rolling?: {
    rollingWindowSize: number;
  };
  // NOTE: This is used for saved formulas to indicate that this multiAttribution model will be added to all
  // referenced metrics. This will never be saved to the formula in appdb; instead, it will always be a
  // report-specific override.
  multiAttribution?: MultiAttribution;
};

export type FormulaShowClause = {
  formula: ShowClauseFormula;
  measurement?: FormulaMeasurement;
  isHidden?: boolean;
  isExpanded?: boolean;
};

export type OldShowClause = {
  /* measurement */
  math?: BehaviorMetric['measurement']['math'];
  perUserAggregation?: BehaviorMetric['measurement']['perUserAggregation'];
  property?: BehaviorMetric['measurement']['property'];
  rolling?: BehaviorMetric['measurement']['rolling'];
  cumulative?: BehaviorMetric['measurement']['cumulative'];
  segmentMethod?: BehaviorMetric['measurement']['segmentMethod'];
  multiAttribution?: BehaviorMetric['measurement']['multiAttribution'];
  /* behavior */
  search?: BehaviorMetric['behavior']['search'];
  resourceType: BehaviorMetric['behavior']['resourceType'];
  profileType?: BehaviorMetric['behavior']['profileType'];
  value: {
    dataset?: string;
    name?: string;
    // start | end
    session_event?: string;
    renamed?: string;
    custom?: boolean;
    id?: number;
    resourceType?: InsightsResourceType;
    // related to autotrack feature
    is_collect_everything_event?: boolean;
    // inline cohort
    raw_cohort?: TODO;
    // saved cohort, seems like not used anymore
    cohort?: {
      name?: string;
      raw_cohort: TODO;
      negated: boolean;
    };
  };
  filter?: FilterClause[];
  filterDeterminer?: BehaviorMetric['behavior']['filtersDeterminer'];
  dataGroupId?: BehaviorMetric['behavior']['dataGroupId'];
  dataset?: BehaviorMetric['behavior']['dataset'];
  datasetId?: BehaviorMetric['behavior']['datasetId'];
  projectId?: BehaviorMetric['behavior']['projectId'];
  customBucket?: BehaviorMetric['behavior']['customBucket'];
};

// for now we are using the old format AND new format for the show clauses
// using & (intersection) operator here to ease some pain for the migration
export type BehaviorShowClause = Partial<BehaviorMetric & OldShowClause>;
export type ShowClause = BehaviorShowClause | FormulaShowClause;

export interface GroupByCohort {
  dataset?: any;
  id?: number;
  name?: string;
  negated?: boolean;
  raw_cohort?: {
    unsavedId?: string;
    behaviors: SerializedBehaviorDictionary;
    selector: Expression;
    data_group_id?: string;
  };
}

export interface GroupClause {
  behavior?: Behavior;
  cohorts?: GroupByCohort[];
  customBucket?: Bucket;
  customGroups?: TODO;
  customProperty?: CustomProperty;
  customPropertyId?: number | string;
  dataGroupId?: string;
  dataset?: string;
  datasetId?: string;
  editing?: boolean;
  joinPropertyType?: PropertyType;
  listItemGroup?: GroupClause;
  listQuantifier?: ListQuantifier;
  profileType?: string;
  profileHistoryOptions?: ProfileHistoryOptions;
  projectId?: number;
  propertyObjectKey?: string | string[];
  propertyType?: PropertyType;
  resourceType: InsightsResourceType;
  search?: string;
  startStepNumber?: number;
  endStepNumber?: number;
  // when tempDataGroupId is set, it's pointing to the lookup table e.g. ['Distinct ID', 'Mapped Property']
  tempDataGroupId?: string;
  typeCast?: PropertyType;
  unit?: TimeUnit;
  value?: string | string[];
  scdEvent?: string;
}

export interface FormulaClause {
  value: string;
  label?: string;
}

export interface TimeClause {
  unit: TimeUnit;
  value?: number | string | [BaseDate] | [BaseDate, BaseDate];
  from_date?: BaseDate;
  to_date?: BaseDate;
  rolling?: boolean;
  window?: {
    unit: TimeUnit;
    value: number;
  };
  dateRangeType?: DateRangeType;
  dataset?: string;
  time_aggregation_unit?: TimeUnit;
  as_of_timestamp?: number;
  time_comparison?: TODO;
  lift_comparison?: TODO;
}

export interface FilterClause {
  dataset: string;
  value: string;
  resourceType: InsightsResourceType;
  // it's always null
  profileType: null;
  search: string;
  dataGroupId: PropertyFilter['dataGroupId'];
  // it is not the same shape as PropertyFilter['filterValue']
  filterValue: TODO;
  behavior?: PropertyFilter['behavior'];
  filterType: PropertyFilter['propertyType'];
  defaultType?: PropertyFilter['propertyDefaultType'];
  filterOperator: PropertyFilter['filterOperator'];
  propertyObjectKey?: PropertyFilter['propertyObjectKey'];
  filterJoinType?: PropertyFilter['joinPropertyType'];
  listQuantifier?: PropertyFilter['listQuantifier'];
  listItemFilters?: PropertyFilter['listItemFilters'];
  filterDateUnit?: PropertyFilter['filterDateUnit'];
  customPropertyId?: PropertyFilter['customPropertyId'];
  customProperty?: PropertyFilter['customProperty'];
  profileHistoryOptions?: ProfileHistoryOptions;
  determiner?: FilterDeterminer;
  // when tempDataGroupId is set, it's pointing to the lookup table e.g. ['Distinct ID', 'Mapped Property']
  tempDataGroupId?: PropertyFilter['tempDataGroupId'];
  scdEvent?: string;
}

export interface InsightsBookmarkSections {
  show: ShowClause[];
  cohorts?: TODO[];
  group_by?: Segmentation[];
  group?: GroupClause[];
  filter?: TODO[];
  formula?: FormulaClause[];
  time: TimeClause[];
  globalDataGroupId?: string;
}

export interface InsightsBookmarkParams {
  columnWidths: {
    // percentages (bar chart is fixed width)
    bar: {[columnIdx: string]: number};
  };
  displayOptions: DisplayOptions;
  name?: string;
  sections: InsightsBookmarkSections;
  sorting: Partial<InsightsBookmarkSortConfig>;
  timeComparison?: ComparisonDateRangeBookmarkFormat;
  liftComparison?: LiftComparison;
  legend?: InsightsLegend;
}
export function isInsightsBookmarkParams(params: any): params is InsightsBookmarkParams {
  return params && params.sections && params.sections.show;
}
export type InsightsChartParams = Pick<InsightsBookmarkParams, 'sections' | 'displayOptions' | 'liftComparison'>;

export type InsightsSeriesTree<ValueT = number> = {[k: string]: ValueT} | {[k: string]: InsightsSeriesTree};

interface DateRange {
  // sic; this is NOT BookmarkDateRange or a similar bookmark-related type
  from_date: string;
  to_date: string;
}

interface IncompleteBuckets {
  [date: string]: Array<null | string>;
}

export interface MetricMetadata {
  metric_key?: string;
  sub_queries?: Array<{metric_key: string}>;
  retention_buckets_size?: number;
  incomplete_buckets?: IncompleteBuckets;
  past_incomplete_buckets?: IncompleteBuckets;
  /*
    Augmented on the client-side by queries/insights based on query limits and segments in the result
  */
  apiResultsTruncated?: boolean;
}

export interface ReportSectionsMetadata {
  cohorts?: Array<{bookmark: GroupByCohort}>;
  group?: Array<{bookmark: GroupClause; numeric_buckets?: Bucket}>;
  show?: MetricMetadata[];
  formula?: MetricMetadata[];
}

export type InsightsSegmentation = Omit<Segmentation, 'resourceType'> & {resourceType: InsightsResourceType};

export interface SegmentationQueryLimits {
  limit?: number;
}

export interface InsightsQueryError extends BaseQueryErrorType {
  formula_errors?: Array<{index: number; message: string}>;
}

export interface InsightsQueryResults extends BaseQueryResultsType {
  date_range: DateRange;
  headers: string[];
  meta: {
    min_sampling_factor: number;
    report_sections: ReportSectionsMetadata;
    /*
    Augmented on the client-side to carry the limits used on the query that generated these results.
    When limits affect the results, we need to tell the user where the cutoff is.
    */
    queryLimits?: SegmentationQueryLimits;
    apiResultsTruncated?: boolean;
    liftComparison?: any;
    time_period_settings?: {quarter_start_month: number};
    // retention specific meta data
    incomplete_buckets?: any;
  };
  series: InsightsSeriesTree;
  forecast_comparison?: {
    series: InsightsSeriesTree<ForecastValue>;

    // these properties are intended for manual debugging and shouldn't be referenced in non-experimental code, they will likely be removed after GA
    date_range: {
      historical_from_date: string;
      historical_to_date: string;
    };
    historical_series: InsightsSeriesTree;
  };
  time_comparison?: {
    date_range: DateRange;
    series: InsightsSeriesTree;
  };
  lift_comparison?: {
    series: InsightsSeriesTree;
  };
}

export interface AggregatedSeriesValue {
  // list of all possible paths to reach the segment in this series
  paths: string[][];

  // sum of all of this segments values in this series
  total: number;
}

export interface AggregatedSeries {
  [label: string]: AggregatedSeriesValue;
}

export interface InsightsDeprecatedLegendUrlData {
  idxSelections?: number[] | number[][];
}

export interface InsightsLegend {
  // used for flat data, entire selected segments
  individualSelectedSegments?: IndividualSelectedSegments;
  // used for nested (bar chart) data, filtered segments at each level
  filteredBreakdownSegments?: FilteredBreakdownSegments;
}
